/**
 * Detects whether the renderer is running inside the Electron shell.
 * The desktop preload exposes `window.fluxter` via `contextBridge`.
 */
export const isElectron = (): boolean =>
  typeof globalThis !== 'undefined' &&
  typeof (globalThis as { window?: { fluxter?: unknown } }).window !== 'undefined' &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Boolean((globalThis as any).window?.fluxter);

/**
 * Mirrors Node's `NodeJS.Platform` union without depending on `@types/node`
 * (the shared package is browser-targeted). The set is fixed by Node and
 * Electron forwards it verbatim.
 */
export type ElectronHostPlatform =
  | 'aix'
  | 'darwin'
  | 'freebsd'
  | 'linux'
  | 'openbsd'
  | 'sunos'
  | 'win32'
  | 'cygwin'
  | 'netbsd';

/**
 * Returns the host OS as reported by Electron's `process.platform`,
 * or `null` when the renderer is running in a regular browser.
 *
 * Used for platform-conditional chrome — the macOS shell needs to
 * reserve space for the inset traffic-light buttons that overlap the
 * top of the window when `titleBarStyle: 'hiddenInset'` is in effect.
 * Windows / Linux don't have this problem (custom buttons or none).
 */
export const electronPlatform = (): ElectronHostPlatform | null => {
  if (!isElectron()) return null;
  const bridge = (globalThis as { window?: { fluxter?: { platform?: ElectronHostPlatform } } })
    .window?.fluxter;
  return bridge?.platform ?? null;
};

/** Convenience: true only when running inside Electron on macOS. */
export const isMacElectron = (): boolean => electronPlatform() === 'darwin';
